import { getAccessToken, EnvTypes } from '../base-utils';
import { handleMessage } from './message-handler';
import { SocketMessage } from './socket.types';

const logger = (message: string, data?: unknown) => {
    if (process.env.VITE_ENV_TYPE !== EnvTypes.PRODUCTION) {
        console.log(message, data);
    }
};

let socket: WebSocket | null = null;
let pendingSubscriptions: string[] = [];

const sendMessage = (data: object) => {
    if (socket?.readyState !== WebSocket.OPEN) {
        logger('Socket not open, cannot send message.', data);
        return;
    }
    socket.send(JSON.stringify(data));
};

export const sendSocketMessage = (message: SocketMessage, channel?: string) => {
    sendMessage({ ...message, channel: channel || undefined });
};

export const connectToSocket = () => {
    if (
        socket?.readyState === WebSocket.OPEN ||
        socket?.readyState === WebSocket.CONNECTING
    ) {
        return;
    }

    const token = getAccessToken();
    if (!token) {
        logger('No token found, aborting connection.');
        return;
    }

    const socketUrl = `${import.meta.env.VITE_WEBSOCKET_URL}?token=${token}`;
    socket = new WebSocket(socketUrl);

    socket.onopen = () => {
        logger('Socket connected');
        subscribeToChannel(pendingSubscriptions);
        pendingSubscriptions = [];
    };

    socket.onclose = (event: CloseEvent) => {
        logger('Socket disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
        });
        socket = null;
    };

    socket.onerror = (error) => {
        logger('Socket error', error);
    };

    socket.onmessage = (event) => {
        try {
            const payload: SocketMessage = JSON.parse(event.data);
            handleMessage(payload);
        } catch (error) {
            console.error('Error parsing incoming socket message:', error);
        }
    };
};

export const subscribeToChannel = (channelNames: Array<string>) => {
    if (socket?.readyState === WebSocket.OPEN) {
        channelNames.forEach((channelName) => {
            sendMessage({
                title: 'SUBSCRIBE',
                data: { group_name: channelName },
            });
            logger(`Sent subscribe request for channel: ${channelName}`);
        });
    } else {
        channelNames.forEach((channelName) => {
            if (!pendingSubscriptions.includes(channelName)) {
                pendingSubscriptions.push(channelName);
            }
        });
        connectToSocket();
    }
};

export const unsubscribeFromChannel = (channelName: string) => {
    pendingSubscriptions = pendingSubscriptions.filter(
        (pending) => pending !== channelName
    );

    if (socket?.readyState === WebSocket.OPEN) {
        sendMessage({
            event: 'UNSUBSCRIBE',
            data: { group_name: channelName },
        });
        logger(`Sent unsubscribe request for channel: ${channelName}`);
    }
};

export const disconnectFromSocket = () => {
    if (socket) {
        socket.close();
    }
};
